import * as Yup from "yup";
import { TBitValue, TReactSetState, TUseTranslationTfn } from "../../data/AppType";
import {
  GET_SUBSCRIPTION_PACKAGE_DETAIL,
  UPDATE_SUBSCRIPTION_PACKAGE,
} from "../../services/Endpoints";
import {
  IPackageConfigurationPayload,
  ISubscriptionPackagePayload,
} from "../../services/subscription-package/SubscriptionPackageService";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import {
  handleError,
  prepareMessageFromParams,
  sweetAlertError,
  sweetAlertSuccess,
} from "../../utils/AppFunctions";
import { requiredNumberRangeSchema } from "../../utils/PurchaseValidation";
import {
  CONTACT_TO_SALES_CONFIG_VALUE,
  CREDIT_BALANCE_MAX,
  PACKAGE_CREDIT_KEYS,
  PACKAGE_DESCRIPTION_MAX_LENGTH,
  PACKAGE_LIMIT_KEYS,
  PRICE_MAX,
  PURCHASE_NAME_MAX_LENGTH,
  TAX_PERCENT_MAX,
  TWO_DECIMALS_REGEX,
  VALIDITY_DAYS_MAX,
} from "../../utils/PurchaseConstants";
import {
  CONFIG_VALUE_MODE_ENUM,
  PACKAGE_CONFIG_KEY_ENUM,
  STOCK_VALUATION_METHOD_ENUM,
  VISIBILITY_TYPE_ENUM,
} from "../../utils/PurchaseEnumerations";

export interface IAvailableFeature {
  id: string;
  feature_title: string;
  help_text: string | null;
}

export interface IAvailableClaim {
  id: string;
  claim_key: string;
  claim_name: string;
  claim_groups: string | null;
}

export interface ISubscriptionPackageDetail {
  id: string;
  name: string;
  description: string | null;
  visibility_type: VISIBILITY_TYPE_ENUM;
  is_active: TBitValue;
  validity: number;
  base_price: string;
  cgst: string;
  sgst: string;
  igst: string;
  total: string;
  platforms: string[];
  has_desktop: boolean;
  configurations: { config_key: string; config_value: string | null }[];
  features: { feature_id: string; annotation: string | null }[];
  claim_ids: string[];
  available_features: IAvailableFeature[];
  available_claims: IAvailableClaim[];
}

export interface IConfigValue {
  mode: CONFIG_VALUE_MODE_ENUM;
  value: string;
}

interface IFeatureValue {
  included: boolean;
  annotation: string;
}

export interface IPackageEditorForm {
  name: string;
  description: string;
  visibility_type: VISIBILITY_TYPE_ENUM;
  has_desktop: boolean;
  validity: number | null;
  base_price: number | null;
  cgst: number | null;
  sgst: number | null;
  igst: number | null;
  configs: Record<string, IConfigValue>;
  stock_valuation: { mode: CONFIG_VALUE_MODE_ENUM; methods: string[] };
  features: Record<string, IFeatureValue>;
  claim_ids: string[];
}

export const VALUE_FEATURE_CONFIG_KEY: Record<string, PACKAGE_CONFIG_KEY_ENUM> = {
  "Employee Limit": PACKAGE_CONFIG_KEY_ENUM.NumberOfUsers,
  "Number of Roles": PACKAGE_CONFIG_KEY_ENUM.NumberOfRoles,
  "Branch Limit": PACKAGE_CONFIG_KEY_ENUM.NumberOfBranches,
  "Firm Limit": PACKAGE_CONFIG_KEY_ENUM.NumberOfFirms,
  "Godown / Warehouse Limit": PACKAGE_CONFIG_KEY_ENUM.NumberOfGodowns,
  "OCR Scan Limit": PACKAGE_CONFIG_KEY_ENUM.NumberOfOcrScans,
  "AI Bot Request Limit": PACKAGE_CONFIG_KEY_ENUM.NumberOfAiBotRequests,
  "WhatsApp Credits": PACKAGE_CONFIG_KEY_ENUM.NumberOfWhatsappCredits,
  "E-Invoice Credits": PACKAGE_CONFIG_KEY_ENUM.NumberOfEInvoiceCredits,
  "E-Way Bill Credits": PACKAGE_CONFIG_KEY_ENUM.NumberOfEWayBillCredits,
  "AI Credits": PACKAGE_CONFIG_KEY_ENUM.NumberOfAiCredits,
};

export const MANAGE_SUBSCRIPTION_CLAIM_KEY = "manage_subscription";

const CONFIG_KEYS: readonly string[] = [...PACKAGE_LIMIT_KEYS, ...PACKAGE_CREDIT_KEYS];
const WHOLE_NUMBER_REGEX = /^\d+$/;
const toConfigValue = (value: string | null | undefined): IConfigValue => {
  if (value === undefined) return { mode: CONFIG_VALUE_MODE_ENUM.NotProvided, value: "" };
  if (value === null) return { mode: CONFIG_VALUE_MODE_ENUM.Unlimited, value: "" };
  if (value === CONTACT_TO_SALES_CONFIG_VALUE) {
    return { mode: CONFIG_VALUE_MODE_ENUM.ContactToSales, value: "" };
  }
  return { mode: CONFIG_VALUE_MODE_ENUM.Number, value };
};

const toPayloadValue = (config: IConfigValue): string | null =>
  config.mode === CONFIG_VALUE_MODE_ENUM.Unlimited
    ? null
    : config.mode === CONFIG_VALUE_MODE_ENUM.ContactToSales
      ? CONTACT_TO_SALES_CONFIG_VALUE
      : config.value.trim();

const requiredMessage = (t: TUseTranslationTfn, fieldName: string) =>
  prepareMessageFromParams(t("error_message_required"), [["fieldName", fieldName]]);

const twoDecimalSchema = (t: TUseTranslationTfn, fieldName: string, max: number) =>
  requiredNumberRangeSchema(t, { fieldName, min: 0, max })
    .test("two-decimals", t("price_invalid_amount"), (value) =>
      value === undefined ? true : TWO_DECIMALS_REGEX.test(String(value)),
    );

const toConfigurationsPayload = (values: IPackageEditorForm): IPackageConfigurationPayload[] => {
  const rows: IPackageConfigurationPayload[] = CONFIG_KEYS.flatMap((key) => {
    const config = values.configs[key];
    return config && config.mode !== CONFIG_VALUE_MODE_ENUM.NotProvided
      ? [{ config_key: key, config_value: toPayloadValue(config) }]
      : [];
  });
  const stock = values.stock_valuation;
  if (stock.mode !== CONFIG_VALUE_MODE_ENUM.NotProvided) {
    rows.push({
      config_key: PACKAGE_CONFIG_KEY_ENUM.AllowedStockValuationMethods,
      config_value:
        stock.mode === CONFIG_VALUE_MODE_ENUM.Unlimited
          ? null
          : Object.values(STOCK_VALUATION_METHOD_ENUM)
              .filter((method) => stock.methods.includes(method))
              .join(","),
    });
  }
  return rows;
};

const toPayload = (values: IPackageEditorForm): ISubscriptionPackagePayload => ({
  name: values.name.trim(),
  description: values.description.trim() || null,
  visibility_type: values.visibility_type,
  has_desktop: values.has_desktop,
  validity: Number(values.validity),
  base_price: Number(values.base_price),
  cgst: Number(values.cgst),
  sgst: Number(values.sgst),
  igst: Number(values.igst),
  configurations: toConfigurationsPayload(values),
  features: Object.entries(values.features)
    .filter(([, feature]) => feature.included)
    .map(([featureId, feature]) => ({
      feature_id: featureId,
      annotation: feature.annotation.trim() || null,
    })),
  claim_ids: values.claim_ids,
});

export const isFreeTrialPackage = (detail: ISubscriptionPackageDetail) =>
  detail.visibility_type === VISIBILITY_TYPE_ENUM.FreeTrial;

export const allowsContactToSales = (visibilityType: string) =>
  visibilityType === VISIBILITY_TYPE_ENUM.ContactToSale ||
  visibilityType === VISIBILITY_TYPE_ENUM.Private;

export const getPackageEditorInitialValues = (
  detail: ISubscriptionPackageDetail,
): IPackageEditorForm => {
  const configByKey = (key: string) =>
    detail.configurations.find((config) => config.config_key === key)?.config_value;
  const stock = detail.configurations.find(
    (config) => config.config_key === PACKAGE_CONFIG_KEY_ENUM.AllowedStockValuationMethods,
  );
  const featureIds = detail.available_features.map((feature) => feature.id);
  const claimIds = detail.available_claims.map((claim) => claim.id);

  return {
    name: detail.name,
    description: detail.description ?? "",
    visibility_type: detail.visibility_type,
    has_desktop: detail.has_desktop,
    validity: Number(detail.validity),
    base_price: Number(detail.base_price),
    cgst: Number(detail.cgst),
    sgst: Number(detail.sgst),
    igst: Number(detail.igst),
    configs: Object.fromEntries(CONFIG_KEYS.map((key) => [key, toConfigValue(configByKey(key))])),
    stock_valuation: !stock
      ? { mode: CONFIG_VALUE_MODE_ENUM.NotProvided, methods: [] }
      : stock.config_value === null
        ? { mode: CONFIG_VALUE_MODE_ENUM.Unlimited, methods: [] }
        : {
            mode: CONFIG_VALUE_MODE_ENUM.Number,
            methods: stock.config_value.split(",").map((method) => method.trim()),
          },
    features: Object.fromEntries(
      detail.features
        .filter((feature) => featureIds.includes(feature.feature_id))
        .map((feature) => [
          feature.feature_id,
          { included: true, annotation: feature.annotation ?? "" },
        ]),
    ),
    claim_ids: detail.claim_ids.filter((id) => claimIds.includes(id)),
  };
};

export const packageEditorValidationSchema = (t: TUseTranslationTfn) =>
  Yup.object().shape({
    name: Yup.string()
      .trim()
      .required(requiredMessage(t, t("name")))
      .min(
        2,
        prepareMessageFromParams(t("error_message_min_length"), [
          ["fieldName", t("name")],
          ["min", "2"],
        ]),
      )
      .max(
        PURCHASE_NAME_MAX_LENGTH,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("name")],
          ["max", String(PURCHASE_NAME_MAX_LENGTH)],
        ]),
      ),
    description: Yup.string()
      .trim()
      .max(
        PACKAGE_DESCRIPTION_MAX_LENGTH,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("description")],
          ["max", String(PACKAGE_DESCRIPTION_MAX_LENGTH)],
        ]),
      ),
    visibility_type: Yup.string().required(requiredMessage(t, t("type"))),
    validity: requiredNumberRangeSchema(t, {
      fieldName: t("validity_days"),
      min: 1,
      max: VALIDITY_DAYS_MAX,
    }).integer(
      prepareMessageFromParams(t("error_message_integer"), [["fieldName", t("validity_days")]]),
    ),
    base_price: twoDecimalSchema(t, t("base_price"), PRICE_MAX),
    cgst: twoDecimalSchema(t, t("cgst_percentage"), TAX_PERCENT_MAX),
    sgst: twoDecimalSchema(t, t("sgst_percentage"), TAX_PERCENT_MAX),
    igst: twoDecimalSchema(t, t("igst_percentage"), TAX_PERCENT_MAX),
  });

export const getPackageContentError = (
  t: TUseTranslationTfn,
  values: IPackageEditorForm,
  labelOf: (key: string) => string,
): string | null => {
  for (const key of CONFIG_KEYS) {
    const config = values.configs[key];
    if (!config) continue;
    if (
      config.mode === CONFIG_VALUE_MODE_ENUM.ContactToSales &&
      !allowsContactToSales(values.visibility_type)
    ) {
      return prepareMessageFromParams(t("public_contact_sales_error"), [["fields", labelOf(key)]]);
    }
    if (config.mode !== CONFIG_VALUE_MODE_ENUM.Number) continue;
    const isCredit = PACKAGE_CREDIT_KEYS.some((creditKey) => creditKey === key);
    const text = config.value.trim();
    if (!WHOLE_NUMBER_REGEX.test(text) || (isCredit && Number(text) > CREDIT_BALANCE_MAX)) {
      return prepareMessageFromParams(t(isCredit ? "credit_value_error" : "limit_value_error"), [
        ["field", labelOf(key)],
      ]);
    }
  }
  if (
    values.stock_valuation.mode === CONFIG_VALUE_MODE_ENUM.Number &&
    values.stock_valuation.methods.length === 0
  ) {
    return t("stock_valuation_error");
  }
  if (values.claim_ids.length === 0) {
    return t("claims_required_error");
  }
  return null;
};

export const fetchPackageDetail = async (
  id: string,
  setDetail: TReactSetState<ISubscriptionPackageDetail | undefined>,
): Promise<boolean> => {
  try {
    const response = await GET_SUBSCRIPTION_PACKAGE_DETAIL(id);
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS && response.data) {
      setDetail(response.data);
      return true;
    }
    sweetAlertError({ message: response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED });
  } catch (error) {
    handleError(error, "fetchPackageDetail");
  }
  return false;
};

export const handleSubmitPackage = async (
  id: string,
  values: IPackageEditorForm,
  setLoading: TReactSetState<boolean>,
): Promise<boolean> => {
  setLoading(true);
  try {
    const response = await UPDATE_SUBSCRIPTION_PACKAGE(id, toPayload(values));
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({ message: response.message });
      return true;
    }
    sweetAlertError({ message: response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED });
  } catch (error) {
    handleError(error, "handleSubmitPackage");
  } finally {
    setLoading(false);
  }
  return false;
};
